/**
 * Cosmic Theme - Shared color palette and CSS utilities for all screens, HUD, and renderers.
 *
 * Design system: deep cosmic black background, neon yellow accents, nebula effects.
 * Fonts: PramukhRounded (titles), Satoshi (body).
 */

export const NeonColors = {
  // Primary accent (buttons, highlights, player brand) — neon yellow
  primary: '#FBBF24',
  primaryGlow: 'rgba(251,191,36,0.4)',

  // Success (evacuation, health, positive actions)
  success: '#00FF88',
  successGlow: 'rgba(0,255,136,0.4)',

  // Gold (stars, sparks, economy)
  gold: '#FFD700',
  goldGlow: 'rgba(255,215,0,0.4)',

  // Portal (special, portal effects)
  portal: '#8855FF',
  portalGlow: 'rgba(136,85,255,0.4)',

  // Danger (death, red zone, warnings)
  danger: '#FF3366',
  dangerGlow: 'rgba(255,51,102,0.4)',

  // Info (timer, boost, informational)
  info: '#00DDFF',
  infoGlow: 'rgba(0,221,255,0.4)',

  // Background layers — deep cosmic black
  bgDark: '#00020a',
  bgMid: '#050816',
  bgOverlay: 'rgba(0,2,10,0.92)',

  // Text hierarchy
  textBright: '#FFFFFF',
  textMuted: '#94a3b8',
  textDim: '#475569',
} as const

export const TOP5_COLORS = [
  '#FF4455', // #1 neon red
  '#FF66BB', // #2 neon pink
  '#FFDD33', // #3 neon yellow
  '#66FF66', // #4 neon green
  '#44CCFF', // #5 neon blue
] as const

/**
 * Same color palette as NeonColors but as 0x hex numbers for PixiJS renderers.
 */
export const NeonColorsHex = {
  primary: 0xFBBF24,
  primaryGlow: 0xFBBF24,

  success: 0x00FF88,
  successGlow: 0x00FF88,

  gold: 0xFFD700,
  goldGlow: 0xFFD700,

  portal: 0x8855FF,
  portalGlow: 0x8855FF,

  danger: 0xFF3366,
  dangerGlow: 0xFF3366,

  info: 0x00DDFF,
  infoGlow: 0x00DDFF,

  bgDark: 0x00020a,
  bgMid: 0x050816,

  textBright: 0xFFFFFF,
  textMuted: 0x94a3b8,
  textDim: 0x475569,
} as const

/** Font stacks used throughout the UI */
export const FONTS = {
  title: "'PramukhRounded', 'Inter', sans-serif",
  body: "'Satoshi', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
} as const

/**
 * CSS string for neon yellow title text glow (multi-layer, bright).
 */
export function neonTitleGlow(): string {
  return `
    color: #fff7ed;
    text-shadow: 0 0 7px #fff, 0 0 15px #fbbf24, 0 0 30px #fbbf24, 0 0 50px #fbbf24, 0 0 80px #fbbf24;
    font-family: ${FONTS.title};
  `.trim()
}

/**
 * CSS string for a glowing neon button.
 * Rounded-2xl, golden glow, dark text for primary, white text for others.
 */
export function neonButton(color: string, glowColor: string): string {
  const isDark = color === NeonColors.primary || color === NeonColors.gold
  return `
    background: ${color};
    border: none;
    border-radius: 16px;
    color: ${isDark ? '#0f172a' : '#fff'};
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 0 20px ${glowColor}, 0 0 40px ${glowColor};
    transition: box-shadow 0.2s ease, transform 0.1s ease;
    padding: 14px 32px;
    font-size: 16px;
    font-family: ${FONTS.body};
    letter-spacing: 0.15em;
    text-transform: uppercase;
  `.trim()
}

export function isTouchUiMode(): boolean {
  const hasFinePointer = window.matchMedia?.('(pointer: fine)').matches ?? false
  if (hasFinePointer) return false

  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  if (window.Telegram?.WebApp && hasTouch) return true

  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false
  if (hasTouch && coarse) return true

  const ua = navigator.userAgent || ''
  return hasTouch && /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(ua)
}

/**
 * CSS string for glowing neon text with two-layer text-shadow.
 */
export function neonText(color: string, glowColor: string): string {
  return `
    color: ${color};
    text-shadow: 0 0 10px ${glowColor}, 0 0 20px ${glowColor};
  `.trim()
}

/**
 * CSS string for a dark semi-transparent panel with subtle border.
 * Uses cosmic glass-morphism style.
 */
export function neonPanel(options?: { blur?: boolean }): string {
  const blur = options?.blur ?? true
  return `
    background: rgba(0,2,10,0.7);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 16px;
    padding: 16px;
    ${blur ? 'backdrop-filter: blur(12px);' : ''}
  `.trim()
}

/**
 * CSS string for opacity fade transition.
 */
export function fadeTransition(durationMs: number = 300): string {
  return `transition: opacity ${durationMs}ms ease-in-out;`
}

/**
 * Full-screen overlay base style for all screens.
 * Position fixed, flex centered, deep cosmic background, z-index 200.
 */
export const SCREEN_BASE_STYLE = `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${NeonColors.bgOverlay};
  z-index: 200;
  font-family: ${FONTS.body};
  color: ${NeonColors.textBright};
  overflow: hidden;
`.trim()

const COSMIC_BACKGROUND_STYLE_ID = 'star-snake-cosmic-background-style'

function ensureCosmicBackgroundStyles(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(COSMIC_BACKGROUND_STYLE_ID)) return

  const style = document.createElement('style')
  style.id = COSMIC_BACKGROUND_STYLE_ID
  style.textContent = `
    @keyframes cosmic-flow {
      0% { transform: translate(0, 0) scale(1); opacity: 0.4; }
      100% { transform: translate(-150px, 150px) scale(1.4); opacity: 0.2; }
    }
    @keyframes star-drift {
      from { background-position: 0 0; }
      to { background-position: -800px 400px; }
    }
    @keyframes star-drift-slow {
      from { background-position: 0 0; }
      to { background-position: -600px 300px; }
    }
    @keyframes star-pulse {
      0%, 100% { opacity: 0.15; }
      50% { opacity: 0.35; }
    }
    .cosmic-star-field {
      position: absolute; inset: 0;
      background-image:
        radial-gradient(1px 1px at 20px 30px, #fff, rgba(0,0,0,0)),
        radial-gradient(1.5px 1.5px at 150px 100px, #fff, rgba(0,0,0,0)),
        radial-gradient(1px 1px at 280px 240px, #fff, rgba(0,0,0,0)),
        radial-gradient(2px 2px at 450px 50px, #fff, rgba(0,0,0,0)),
        radial-gradient(1px 1px at 600px 320px, #fff, rgba(0,0,0,0)),
        radial-gradient(1.5px 1.5px at 800px 150px, #fff, rgba(0,0,0,0)),
        radial-gradient(1px 1px at 100px 200px, #fff, rgba(0,0,0,0)),
        radial-gradient(1.5px 1.5px at 350px 350px, #fff, rgba(0,0,0,0)),
        radial-gradient(1px 1px at 500px 120px, #fff, rgba(0,0,0,0)),
        radial-gradient(2px 2px at 700px 280px, #fff, rgba(0,0,0,0));
      background-size: 800px 400px;
      pointer-events: none;
    }
    .cosmic-nebula {
      position: absolute;
      border-radius: 50%;
      filter: blur(120px);
      mix-blend-mode: screen;
      animation: cosmic-flow 60s linear infinite;
      pointer-events: none;
    }
  `.trim()
  document.head.appendChild(style)
}

/**
 * Cosmic animated background HTML (star field + nebula layers).
 * Insert as innerHTML at the beginning of any full-screen container.
 */
export function cosmicBackground(): string {
  ensureCosmicBackgroundStyles()
  return `
    <div class="cosmic-star-field" style="opacity: 0.4; animation: star-drift 80s linear infinite;"></div>
    <div class="cosmic-star-field" style="opacity: 0.25; animation: star-drift-slow 120s linear infinite, star-pulse 5s ease-in-out infinite;"></div>
    <div class="cosmic-nebula" style="top: -80px; left: -80px; width: 800px; height: 800px; background: rgba(99,102,241,0.15); animation-duration: 45s;"></div>
    <div class="cosmic-nebula" style="top: 25%; right: -160px; width: 900px; height: 900px; background: rgba(236,72,153,0.10); animation-duration: 75s; animation-delay: -15s;"></div>
    <div class="cosmic-nebula" style="bottom: -160px; left: -160px; width: 1000px; height: 1000px; background: rgba(59,130,246,0.15); animation-duration: 90s; animation-delay: -30s;"></div>
  `
}

/**
 * CSS label style for small uppercase tracking labels.
 */
export function cosmicLabel(): string {
  return `
    color: ${NeonColors.textMuted};
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.3em;
    font-weight: 900;
  `.trim()
}

/**
 * Inline SVG for the Telegram Stars currency icon.
 * Returns an HTML string with an <svg> element sized to `sizePx`.
 * Uses the official Telegram Stars gradient (golden-yellow).
 */
let _tgStarCounter = 0

export function telegramStarIcon(sizePx: number = 18): string {
  const id = `tgs${++_tgStarCounter}`
  return `<svg width="${sizePx}" height="${sizePx}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:inline-block;vertical-align:middle;flex-shrink:0;">
    <defs>
      <linearGradient id="${id}" x1="4" y1="2" x2="20" y2="22" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#FFE14D"/>
        <stop offset="50%" stop-color="#FFBA0A"/>
        <stop offset="100%" stop-color="#FF8C00"/>
      </linearGradient>
    </defs>
    <path d="M12 1.5l3.09 6.26L22 8.77l-5 4.87 1.18 6.86L12 17.27 5.82 20.5 7 13.64l-5-4.87 6.91-1.01L12 1.5z" fill="url(#${id})" stroke="#FFD700" stroke-width="0.5" stroke-linejoin="round"/>
  </svg>`
}

/**
 * Shorthand for Telegram Star icon used in text: icon + optional text.
 * Example: `tgStar(18)` returns just the icon, `tgStar(14, '5')` returns "5 ⭐"
 */
export function tgStar(sizePx: number = 16, text?: string | number): string {
  const icon = telegramStarIcon(sizePx)
  if (text !== undefined) return `${text}&nbsp;${icon}`
  return icon
}
